import {AlertEpisodeDisplayedState} from './alert_types';

/**
 * The board's dynamics chart (PDW-248 / FE PDW-249): the episodes the viewer may see, each counted once in the month it
 * first became active and in its displayed state today. The shapes follow core's `AlertBoardChartDtos.kt`, answered
 * unchanged by MERIE's `GET /api/alerts/board/chart`.
 */

/** What the series split the episodes by: one series per displayed state, or the top five alerts and Other. */
export type AlertBoardChartBreakdown = 'STATE' | 'ALERT';
/** What each point carries: the count alone, or with the count per 1000 care days of its month. */
export type AlertBoardChartValues = 'COUNTS' | 'PER_1000_CARE_DAYS';
/** How the chart is drawn. The FE's alone, kept in the saved filter only. */
export type AlertBoardChartType = 'STACKED_LINE_CHART' | 'LINE_CHART';

/** Each option unset means its first value, as MERIE's service defaults them. */
export const DEFAULT_ALERT_BOARD_CHART_BREAKDOWN: AlertBoardChartBreakdown = 'STATE';
export const DEFAULT_ALERT_BOARD_CHART_VALUES: AlertBoardChartValues = 'COUNTS';
export const DEFAULT_ALERT_BOARD_CHART_TYPE: AlertBoardChartType = 'STACKED_LINE_CHART';

/** MERIE's translated enums of the three options in `GET /api/enums`. */
export const ALERT_BOARD_CHART_BREAKDOWN_ENUM = 'AlertBoardChartBreakdownEnum';
export const ALERT_BOARD_CHART_VALUES_ENUM = 'AlertBoardChartValuesEnum';
export const ALERT_BOARD_CHART_TYPE_ENUM = 'AlertBoardChartTypeEnum';

export type AlertBoardChartPointDto = {
  count: number;
  /** Three decimals, 0 for a month without care days. Absent unless the request asked for it. */
  countPer1000CareDays?: number | null;
};

/**
 * One series. `state` is set by the state breakdown, `userFilterId` and `name` by the alert breakdown, and none of them
 * on its Other series, the alerts past the fifth together.
 */
export type AlertBoardChartSeriesDto = {
  state?: AlertEpisodeDisplayedState | null;
  userFilterId?: number | null;
  name?: string | null;
  /** One per month, `points[i]` belonging to `months[i]`. */
  points: AlertBoardChartPointDto[];
};

/**
 * `months` holds the first day of every month of the window (ISO dates), oldest first: the end month and the twelve
 * before it, or the whole date range when it is longer. A month without an episode is a point at zero, never a gap.
 * With no list the user may open, both are empty.
 */
export type AlertBoardChartDto = {
  months: string[];
  series: AlertBoardChartSeriesDto[];
};
